import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Wine, Grape, BedDouble, User, AtSign, PhoneCall, Send, Clock } from "lucide-react";

const PURPOSES = [
    { v: "general", l: "Obecný dotaz", icon: MessageCircle },
    { v: "wine", l: "Objednávka vín", icon: Wine },
    { v: "tasting", l: "Degustace", icon: Grape },
    { v: "accommodation", l: "Ubytování", icon: BedDouble },
];

const PURPOSE_LABELS = {
    general: "Obecný dotaz",
    wine: "Objednávka vín",
    tasting: "Rezervace degustace",
    accommodation: "Rezervace ubytování",
};

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

async function sendToWeb3Forms(form) {
    if (!WEB3FORMS_KEY) return;
    const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `Vinařství Štrokay · ${PURPOSE_LABELS[form.purpose] || "Zpráva z webu"}`,
        from_name: form.name,
        replyto: form.email,
        name: form.name,
        email: form.email,
        phone: form.phone || "—",
        purpose: PURPOSE_LABELS[form.purpose] || form.purpose,
        message: form.message,
    };
    await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
    });
}

export default function Contact({ prefill }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", purpose: "general", message: "",
    });
    const [sending, setSending] = useState(false);
    const [focus, setFocus] = useState(null);

    useEffect(() => {
        if (prefill) {
            setForm((f) => ({ ...f, purpose: prefill.purpose || f.purpose, message: prefill.message ? prefill.message + (f.message ? "\n\n" + f.message : "") : f.message }));
        }
        // eslint-disable-next-line
    }, [prefill?.message, prefill?.purpose]);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Vyplňte prosím jméno, email a zprávu.");
            return;
        }
        setSending(true);
        try {
            // Send to both channels in parallel: backend (admin log) + web3forms (email)
            await Promise.all([
                api.post("/contact", form),
                sendToWeb3Forms(form).catch(() => null),
            ]);
            toast.success("Děkujeme! Vaši zprávu jsme přijali, ozveme se co nejdříve.");
            setForm({ name: "", email: "", phone: "", purpose: "general", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Odeslání selhalo.");
        } finally {
            setSending(false);
        }
    };

    const inputBase = "w-full bg-transparent border-0 border-b py-3.5 pl-8 pr-2 outline-none text-cream placeholder:text-cream/35 transition-all duration-300";
    const labelStyle = { color: "rgba(247,245,240,0.65)" };
    const labelSubtleStyle = { color: "rgba(247,245,240,0.5)" };

    return (
        <section id="kontakt" data-testid="contact-section" className="py-24 md:py-36 px-6 md:px-12 bg-burgundyDeep text-cream relative overflow-hidden">
            {/* soft decorative glow */}
            <div aria-hidden className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.12] blur-3xl" style={{ background: "radial-gradient(circle, #F7F5F0 0%, transparent 70%)" }} />
            <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-32 w-[560px] h-[560px] rounded-full opacity-[0.08] blur-3xl" style={{ background: "radial-gradient(circle, #F7F5F0 0%, transparent 70%)" }} />

            <div className="relative mx-auto max-w-[1500px] grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-5">
                    <p className="overline mb-6" style={labelStyle}>Kontakt · Napište nám</p>
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-cream mb-8">
                        Ozveme se <span className="font-serif-italic text-cream/85">do 24 hodin</span>
                    </h2>
                    <p className="text-base md:text-lg text-cream/75 leading-relaxed max-w-md mb-10">
                        Objednávka vín, rezervace degustace nebo ubytování — napište nám cokoliv.
                    </p>

                    <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-cream/8 border border-cream/15 backdrop-blur-sm mb-12">
                        <Clock size={14} className="text-cream/70" />
                        <span className="text-[11px] uppercase tracking-[0.22em] text-cream/80">Odpovídáme obvykle do 24 h</span>
                    </div>

                    <ul className="space-y-6 text-cream/85">
                        <li className="flex items-start gap-4 group">
                            <div className="w-11 h-11 flex items-center justify-center bg-cream/8 border border-cream/15 shrink-0 group-hover:bg-cream/15 transition-colors">
                                <Mail size={16} />
                            </div>
                            <div className="pt-1">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-cream/50 mb-1">E-mail</div>
                                <a data-testid="contact-email-link" href="mailto:martinstrokay@gmail.com" className="text-base hover:text-cream transition-colors">martinstrokay@gmail.com</a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4 group">
                            <div className="w-11 h-11 flex items-center justify-center bg-cream/8 border border-cream/15 shrink-0 group-hover:bg-cream/15 transition-colors">
                                <Phone size={16} />
                            </div>
                            <div className="pt-1">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-cream/50 mb-1">Telefon</div>
                                <a data-testid="contact-phone-link" href="tel:+420733266209" className="text-base hover:text-cream transition-colors">+420 733 266 209</a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4 group">
                            <div className="w-11 h-11 flex items-center justify-center bg-cream/8 border border-cream/15 shrink-0 group-hover:bg-cream/15 transition-colors">
                                <MapPin size={16} />
                            </div>
                            <div className="pt-1">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-cream/50 mb-1">Adresa</div>
                                <div className="text-base">Vinařství Štrokay<br />Nechory, 696 21 Josefov-Prušánky</div>
                            </div>
                        </li>
                    </ul>
                </div>

                <form onSubmit={submit} className="lg:col-span-7" data-testid="contact-form">
                    <div className="relative bg-cream/[0.04] border border-cream/12 backdrop-blur-sm p-8 md:p-12 space-y-8">
                        {/* Corner accents */}
                        <span aria-hidden className="absolute -top-px -left-px w-8 h-8 border-t-2 border-l-2 border-cream/40" />
                        <span aria-hidden className="absolute -top-px -right-px w-8 h-8 border-t-2 border-r-2 border-cream/40" />
                        <span aria-hidden className="absolute -bottom-px -left-px w-8 h-8 border-b-2 border-l-2 border-cream/40" />
                        <span aria-hidden className="absolute -bottom-px -right-px w-8 h-8 border-b-2 border-r-2 border-cream/40" />

                        {/* Purpose pills */}
                        <div>
                            <div className="overline mb-4" style={labelStyle}>Důvod kontaktu</div>
                            <div className="flex flex-wrap gap-2">
                                {PURPOSES.map((p) => {
                                    const Icon = p.icon;
                                    const active = form.purpose === p.v;
                                    return (
                                        <button
                                            key={p.v}
                                            type="button"
                                            data-testid={`contact-purpose-${p.v}`}
                                            onClick={() => setForm({ ...form, purpose: p.v })}
                                            className={`inline-flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold border transition-all duration-300 ${
                                                active
                                                    ? "bg-cream text-burgundyDeep border-cream shadow-[0_0_0_4px_rgba(247,245,240,0.08)]"
                                                    : "bg-transparent text-cream/75 border-cream/20 hover:border-cream/60 hover:text-cream"
                                            }`}
                                        >
                                            <Icon size={13} />
                                            {p.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <label className="block relative">
                                <span className="overline" style={labelStyle}>Jméno *</span>
                                <div className="relative mt-2">
                                    <User size={15} className={`absolute left-0 top-4 transition-colors ${focus === "name" ? "text-cream" : "text-cream/40"}`} />
                                    <input
                                        data-testid="contact-input-name"
                                        type="text"
                                        value={form.name}
                                        onChange={change("name")}
                                        onFocus={() => setFocus("name")}
                                        onBlur={() => setFocus(null)}
                                        className={inputBase}
                                        style={{ borderBottomColor: focus === "name" ? "#F7F5F0" : "rgba(247,245,240,0.28)" }}
                                        placeholder="Jan Novák"
                                        required
                                    />
                                </div>
                            </label>
                            <label className="block relative">
                                <span className="overline" style={labelStyle}>E-mail *</span>
                                <div className="relative mt-2">
                                    <AtSign size={15} className={`absolute left-0 top-4 transition-colors ${focus === "email" ? "text-cream" : "text-cream/40"}`} />
                                    <input
                                        data-testid="contact-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={change("email")}
                                        onFocus={() => setFocus("email")}
                                        onBlur={() => setFocus(null)}
                                        className={inputBase}
                                        style={{ borderBottomColor: focus === "email" ? "#F7F5F0" : "rgba(247,245,240,0.28)" }}
                                        placeholder="jan@email.cz"
                                        required
                                    />
                                </div>
                            </label>
                        </div>

                        <label className="block relative">
                            <span className="overline" style={labelStyle}>Telefon</span>
                            <div className="relative mt-2">
                                <PhoneCall size={15} className={`absolute left-0 top-4 transition-colors ${focus === "phone" ? "text-cream" : "text-cream/40"}`} />
                                <input
                                    data-testid="contact-input-phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={change("phone")}
                                    onFocus={() => setFocus("phone")}
                                    onBlur={() => setFocus(null)}
                                    className={inputBase}
                                    style={{ borderBottomColor: focus === "phone" ? "#F7F5F0" : "rgba(247,245,240,0.28)" }}
                                    placeholder="+420 …"
                                />
                            </div>
                        </label>

                        <label className="block">
                            <div className="flex items-baseline justify-between mb-2">
                                <span className="overline" style={labelStyle}>Zpráva *</span>
                                <span className="text-[10px] uppercase tracking-[0.2em]" style={labelSubtleStyle}>{form.message.length} znaků</span>
                            </div>
                            <textarea
                                data-testid="contact-input-message"
                                value={form.message}
                                onChange={change("message")}
                                onFocus={() => setFocus("message")}
                                onBlur={() => setFocus(null)}
                                rows={5}
                                className="w-full bg-transparent border-0 border-b py-3.5 outline-none resize-none text-cream placeholder:text-cream/35 transition-colors"
                                style={{ borderBottomColor: focus === "message" ? "#F7F5F0" : "rgba(247,245,240,0.28)" }}
                                placeholder="Kolik lidí přijde na degustaci, kolik lahví byste chtěli, na kdy…"
                                required
                            />
                        </label>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                            <p className="text-[11px] text-cream/50 max-w-xs">
                                Odesláním souhlasíte s tím, že vás na uvedený kontakt zpětně oslovíme.
                            </p>
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={sending}
                                className="group relative inline-flex items-center gap-3 bg-cream text-burgundyDeep px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-semibold overflow-hidden transition-all duration-300 hover:pr-11 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <span>{sending ? "Odesílám…" : "Odeslat zprávu"}</span>
                                <Send
                                    size={15}
                                    className="transition-all duration-300 group-hover:translate-x-1"
                                />
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Full-width map */}
            <div className="relative mx-auto max-w-[1500px] mt-16 md:mt-24" data-testid="contact-map">
                <p className="overline mb-5 text-cream/70">Kde nás najdete</p>
                <div className="border border-cream/15 overflow-hidden">
                    <iframe
                        title="Mapa - Vinařství Štrokay, Nechory"
                        src="https://www.google.com/maps?q=Vina%C5%99stv%C3%AD+%C5%A0trokay,+Nechory,+Pru%C5%A1%C3%A1nky&ll=48.8428024,16.986716&z=17&output=embed"
                        width="100%"
                        height="520"
                        style={{ border: 0, display: "block" }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                    ></iframe>
                    <div className="flex flex-col sm:flex-row gap-px bg-cream/15">
                        <a
                            href="https://www.google.com/maps/place/Vina%C5%99stv%C3%AD+%C5%A0trokay/@48.8428024,16.986716,17z/data=!4m6!3m5!1s0x4713295e6484428f:0xf92301afef37677c!8m2!3d48.8428024!4d16.986716!16s%2Fg%2F11zjqqf2r4"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-map-mapy"
                            className="flex-1 bg-cream/5 hover:bg-cream/15 px-4 py-4 text-[11px] uppercase tracking-[0.22em] text-cream/85 hover:text-cream transition-colors text-center"
                        >
                            Otevřít v Google Maps →
                        </a>
                        <a
                            href="https://www.google.com/maps/dir/?api=1&destination=Vina%C5%99stv%C3%AD+%C5%A0trokay&destination_place_id=ChIJj0KEZF4pE0cRfGc379p2I_kV"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-map-directions"
                            className="flex-1 bg-cream/5 hover:bg-cream/15 px-4 py-4 text-[11px] uppercase tracking-[0.22em] text-cream/85 hover:text-cream transition-colors text-center"
                        >
                            Naplánovat trasu →
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
